import React from 'react';
import { X, Download, UploadCloud } from 'lucide-react';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api/client';
import { useToast } from '../ui/Toast';

interface BulkUploadRequirementsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Required and optional columns for CSV template
const REQUIRED_COLUMNS = [
  'title',
  'client_name',
  'status', // active|draft|on_hold|closed
  'seniority', // entry|mid-level|senior|lead
  'employment_type', // full-time|contract|part-time|internship
  'remote_type', // onsite|remote|hybrid
  'location_city',
  'location_state',
  'location_country',
  'positions',
  'min_experience_years',
  'max_experience_years',
  'mandatory_skills', // comma-separated
  'education',
  'description'
];

const OPTIONAL_COLUMNS = [
  'optional_skills', // comma-separated
  'salary_min',
  'salary_max',
  'currency', // e.g., INR, USD
  'shift', // day|night|flex
  'priority', // low|medium|high|urgent
  'tags', // comma-separated
  'target_joining_date', // YYYY-MM-DD
  'external_id',
  'notes'
];

const CSV_HEADERS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];

function toCsvRow(values: Array<string | number | undefined>) {
  return values
    .map((v) => {
      if (v === undefined || v === null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    })
    .join(',');
}

function generateCsvContent() {
  const example1 = [
    'Senior Backend Engineer',
    'Acme Corp',
    'active',
    'senior',
    'full-time',
    'hybrid',
    'Bengaluru',
    'Karnataka',
    'India',
    3,
    5,
    8,
    'Python, FastAPI, PostgreSQL, AWS',
    'B.Tech or equivalent',
    'Own backend services, API design, performance and reliability',
    'Redis, Docker, Kubernetes',
    2500000,
    3500000,
    'INR',
    'day',
    'high',
    'backend,platform,api',
    '2025-12-01',
    'ACME-ENG-001',
    'Experience with event-driven architecture preferred'
  ];

  const example2 = [
    'Frontend Developer',
    'Globex',
    'active',
    'mid-level',
    'contract',
    'remote',
    'Pune',
    'Maharashtra',
    'India',
    2,
    3,
    6,
    'React, TypeScript, CSS',
    'Any graduate',
    'Build responsive UI, integrate APIs, maintain component library',
    'Vite, TailwindCSS, Jest',
    1200000,
    1800000,
    'INR',
    'flex',
    'medium',
    'frontend,ui,web',
    '2026-01-15',
    'GLOBEX-FE-007',
    'Good to have: charting libraries'
  ];

  const lines = [CSV_HEADERS.join(','), toCsvRow(example1), toCsvRow(example2)];
  return lines.join('\n');
}

export const BulkUploadRequirementsModal: React.FC<BulkUploadRequirementsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  const { toast } = useToast();
  const [uploading, setUploading] = React.useState(false);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const res = await apiClient.bulkUploadRequirementsCsv(file);
      toast({
        title: 'Bulk upload processed',
        description: `Created: ${res.created}, Errors: ${res.errors}`,
        variant: res.errors > 0 ? 'warning' : 'success',
        duration: 6000
      });
      onClose();
    } catch (err: any) {
      toast({
        title: 'Upload failed',
        description: err?.response?.data?.detail || 'Failed to process CSV',
        variant: 'error',
        duration: 7000
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const csv = generateCsvContent();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'requirements_bulk_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-[560px] max-w-[92vw] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Bulk upload requirements</h3>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Download a clean CSV template with all required columns needed to create complete JDs. Skills fields accept comma-separated values.
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Required columns (for a complete JD)</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 break-words">
              {REQUIRED_COLUMNS.join(', ')}
            </div>
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mt-3 mb-2">Optional columns</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 break-words">
              {OPTIONAL_COLUMNS.join(', ')}
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">How to use</div>
            <ol className="list-decimal ml-5 space-y-1 text-xs text-gray-600 dark:text-gray-400">
              <li>Download the CSV template.</li>
              <li>Fill in all required columns; use commas for skills.</li>
              <li>Save as .csv (UTF-8).</li>
              <li>Return here to upload the file (upload coming soon).</li>
            </ol>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleDownloadTemplate} className="inline-flex items-center gap-2">
              <Download className="w-4 h-4" />
              Download CSV template
            </Button>
            <label className={`inline-flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <UploadCloud className="w-4 h-4" />
              {uploading ? 'Uploading…' : 'Upload CSV'}
              <input type="file" accept=".csv" className="hidden" onChange={onFileChange} disabled={uploading} />
            </label>
          </div>

          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Tip: Use allowed values for status, seniority, employment_type, and remote_type as shown above.
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadRequirementsModal;


